export const nodeConfigs = {
  input: {
    title: 'Input Configuration',
    fields: [
      {
        id: 'source',
        label: 'Data Source',
        type: 'select',
        options: ['API', 'Database', 'File Upload', 'Stream', 'AI Generation'],
        required: true
      },
      {
        id: 'format',
        label: 'Data Format',
        type: 'select',
        options: ['JSON', 'CSV', 'XML', 'Plain Text'],
        required: true
      },
      {
        id: 'interval',
        label: 'Update Interval',
        type: 'select',
        options: ['Real-time', '1 minute', '5 minutes', '15 minutes', '1 hour', '1 day'],
        required: false
      },
      {
        id: 'aiModel',
        label: 'AI Model',
        type: 'select',
        options: ['GPT-4', 'GPT-3.5', 'Claude', 'Custom'],
        required: (config) => config.source === 'AI Generation',
        showWhen: (config) => config.source === 'AI Generation'
      },
      {
        id: 'prompt',
        label: 'Generation Prompt',
        type: 'textarea',
        placeholder: 'Enter your prompt for AI generation...',
        required: (config) => config.source === 'AI Generation',
        showWhen: (config) => config.source === 'AI Generation'
      },
      {
        id: 'temperature',
        label: 'Temperature',
        type: 'range',
        min: 0,
        max: 1,
        step: 0.1,
        required: false,
        showWhen: (config) => config.source === 'AI Generation'
      },
      {
        id: 'maxTokens',
        label: 'Max Tokens',
        type: 'number',
        min: 1,
        max: 4000,
        placeholder: '1000',
        required: false,
        showWhen: (config) => config.source === 'AI Generation'
      }
    ]
  },
  filter: {
    title: 'Filter Configuration',
    fields: [
      {
        id: 'condition',
        label: 'Filter Condition',
        type: 'text',
        placeholder: 'e.g. value > 100',
        required: true
      },
      {
        id: 'field',
        label: 'Target Field',
        type: 'text',
        required: true
      },
      {
        id: 'caseSensitive',
        label: 'Case Sensitive',
        type: 'checkbox',
        required: false
      }
    ]
  },
  transform: {
    title: 'Transform Configuration',
    fields: [
      {
        id: 'operation',
        label: 'Operation Type',
        type: 'select',
        options: ['Map', 'Reduce', 'Filter', 'Sort', 'Group'],
        required: true
      },
      {
        id: 'expression',
        label: 'Transform Expression',
        type: 'textarea',
        placeholder: 'Enter transformation logic',
        required: true
      }
    ]
  },
  agent: {
    title: 'AI Agent Configuration',
    fields: [
      {
        id: 'model',
        label: 'AI Model',
        type: 'select',
        options: ['GPT-4', 'GPT-3.5', 'Claude', 'Custom'],
        required: true
      },
      {
        id: 'prompt',
        label: 'System Prompt',
        type: 'textarea',
        placeholder: 'Enter system prompt',
        required: true
      },
      {
        id: 'temperature',
        label: 'Temperature',
        type: 'range',
        min: 0,
        max: 1,
        step: 0.1,
        required: false
      }
    ]
  },
  output: {
    title: 'Output Configuration',
    fields: [
      {
        id: 'destination',
        label: 'Data Destination',
        type: 'select',
        options: ['API', 'Database', 'File', 'Stream'],
        required: true
      },
      {
        id: 'format',
        label: 'Output Format',
        type: 'select',
        options: ['JSON', 'CSV', 'XML', 'Plain Text'],
        required: true
      }
    ]
  },
  api: {
    title: 'API Configuration',
    fields: [
      {
        id: 'endpoint',
        label: 'API Endpoint',
        type: 'text',
        required: true
      },
      {
        id: 'method',
        label: 'HTTP Method',
        type: 'select',
        options: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'],
        required: true
      },
      {
        id: 'headers',
        label: 'Headers',
        type: 'textarea',
        placeholder: '{"Content-Type": "application/json"}',
        required: false
      }
    ]
  },
  database: {
    title: 'Database Configuration',
    fields: [
      {
        id: 'type',
        label: 'Database Type',
        type: 'select',
        options: ['PostgreSQL', 'MySQL', 'MongoDB', 'Redis'],
        required: true
      },
      {
        id: 'query',
        label: 'Query',
        type: 'textarea',
        placeholder: 'Enter your query',
        required: true
      }
    ]
  },
  webhook: {
    title: 'Webhook Configuration',
    fields: [
      {
        id: 'url',
        label: 'Webhook URL',
        type: 'text',
        required: true
      },
      {
        id: 'secret',
        label: 'Webhook Secret',
        type: 'password',
        required: false
      }
    ]
  },
  custom: {
    title: 'Custom Node Configuration',
    fields: [
      {
        id: 'code',
        label: 'Custom Code',
        type: 'textarea',
        placeholder: 'Enter your custom code',
        required: true
      },
      {
        id: 'language',
        label: 'Language',
        type: 'select',
        options: ['JavaScript', 'Python', 'Ruby', 'Shell'],
        required: true
      }
    ]
  },
  sort: {
    title: 'Sort Configuration',
    fields: [
      {
        id: 'field',
        label: 'Sort Field',
        type: 'text',
        placeholder: 'Field name to sort by',
        required: true
      },
      {
        id: 'direction',
        label: 'Sort Direction',
        type: 'select',
        options: ['Ascending', 'Descending'],
        required: true
      },
      {
        id: 'nullsPosition',
        label: 'Nulls Position',
        type: 'select',
        options: ['First', 'Last'],
        required: false
      }
    ]
  },
  join: {
    title: 'Join Configuration',
    fields: [
      {
        id: 'joinType',
        label: 'Join Type',
        type: 'select',
        options: ['Inner Join', 'Left Join', 'Right Join', 'Full Outer Join', 'Cross Join'],
        required: true
      },
      {
        id: 'leftField',
        label: 'Left Field',
        type: 'text',
        placeholder: 'Field from left dataset',
        required: true
      },
      {
        id: 'rightField',
        label: 'Right Field',
        type: 'text',
        placeholder: 'Field from right dataset',
        required: true
      },
      {
        id: 'customCondition',
        label: 'Custom Join Condition',
        type: 'textarea',
        placeholder: 'Optional custom join condition',
        required: false
      }
    ]
  },
  merge: {
    title: 'Merge Configuration',
    fields: [
      {
        id: 'mergeType',
        label: 'Merge Type',
        type: 'select',
        options: ['Union', 'Union All', 'Intersect', 'Except'],
        required: true
      },
      {
        id: 'matchFields',
        label: 'Matching Fields',
        type: 'text',
        placeholder: 'Comma-separated field names',
        required: false
      }
    ]
  },
  aggregate: {
    title: 'Aggregate Configuration',
    fields: [
      {
        id: 'groupByFields',
        label: 'Group By Fields',
        type: 'text',
        placeholder: 'Comma-separated field names',
        required: true
      },
      {
        id: 'aggregations',
        label: 'Aggregations',
        type: 'textarea',
        placeholder: 'e.g. SUM(sales) as total_sales, AVG(price) as avg_price',
        required: true
      },
      {
        id: 'having',
        label: 'Having Condition',
        type: 'text',
        placeholder: 'e.g. SUM(sales) > 1000',
        required: false
      }
    ]
  },
  distinct: {
    title: 'Distinct Configuration',
    fields: [
      {
        id: 'fields',
        label: 'Distinct Fields',
        type: 'text',
        placeholder: 'Comma-separated field names',
        required: true
      },
      {
        id: 'keepFirst',
        label: 'Keep First Occurrence',
        type: 'checkbox',
        required: false
      }
    ]
  },
  decisionTree: {
    title: 'Decision Tree Configuration',
    fields: [
      {
        id: 'targetField',
        label: 'Target Field',
        type: 'text',
        placeholder: 'Field to predict',
        required: true
      },
      {
        id: 'featureFields',
        label: 'Feature Fields',
        type: 'text',
        placeholder: 'Comma-separated field names',
        required: true
      },
      {
        id: 'maxDepth',
        label: 'Max Depth',
        type: 'number',
        min: 1,
        max: 20,
        required: false
      },
      {
        id: 'minSamplesSplit',
        label: 'Min Samples Split',
        type: 'number',
        min: 2,
        max: 100,
        required: false
      }
    ]
  },
  sqlBuilder: {
    title: 'SQL Builder Configuration',
    fields: [
      {
        id: 'dialect',
        label: 'SQL Dialect',
        type: 'select',
        options: ['PostgreSQL', 'MySQL', 'SQLite', 'SQL Server', 'Oracle'],
        required: true
      },
      {
        id: 'template',
        label: 'SQL Template',
        type: 'textarea',
        placeholder: 'Enter SQL template with placeholders',
        required: true
      },
      {
        id: 'parameters',
        label: 'Parameters',
        type: 'textarea',
        placeholder: '{"param1": "value1", "param2": "value2"}',
        required: false
      }
    ]
  },
  emailSender: {
    title: 'Email Sender Configuration',
    fields: [
      {
        id: 'to',
        label: 'To',
        type: 'text',
        placeholder: 'recipient@example.com',
        required: true
      },
      {
        id: 'cc',
        label: 'CC',
        type: 'text',
        placeholder: 'cc@example.com',
        required: false
      },
      {
        id: 'bcc',
        label: 'BCC',
        type: 'text',
        placeholder: 'bcc@example.com',
        required: false
      },
      {
        id: 'subject',
        label: 'Subject',
        type: 'text',
        placeholder: 'Email subject',
        required: true
      },
      {
        id: 'body',
        label: 'Body',
        type: 'textarea',
        placeholder: 'Email body content',
        required: true
      },
      {
        id: 'isHtml',
        label: 'HTML Format',
        type: 'checkbox',
        required: false
      },
      {
        id: 'attachments',
        label: 'Attachments',
        type: 'text',
        placeholder: 'Comma-separated file paths or data references',
        required: false
      }
    ]
  },
  emailTemplate: {
    title: 'Email Template Configuration',
    fields: [
      {
        id: 'name',
        label: 'Template Name',
        type: 'text',
        placeholder: 'Monthly Report Template',
        required: true
      },
      {
        id: 'subject',
        label: 'Subject Template',
        type: 'text',
        placeholder: 'Monthly Report - {{month}}',
        required: true
      },
      {
        id: 'bodyTemplate',
        label: 'Body Template',
        type: 'textarea',
        placeholder: 'Dear {{name}},\n\nHere is your monthly report for {{month}}.\n\n{{report_content}}',
        required: true
      },
      {
        id: 'isHtml',
        label: 'HTML Format',
        type: 'checkbox',
        required: false
      }
    ]
  },
  scheduler: {
    title: 'Scheduler Configuration',
    fields: [
      {
        id: 'schedule',
        label: 'Schedule Type',
        type: 'select',
        options: ['One-time', 'Daily', 'Weekly', 'Monthly', 'Custom Cron'],
        required: true
      },
      {
        id: 'time',
        label: 'Time',
        type: 'text',
        placeholder: 'HH:MM',
        required: (config) => config.schedule !== 'Custom Cron',
        showWhen: (config) => config.schedule !== 'Custom Cron'
      },
      {
        id: 'dayOfWeek',
        label: 'Day of Week',
        type: 'select',
        options: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        required: (config) => config.schedule === 'Weekly',
        showWhen: (config) => config.schedule === 'Weekly'
      },
      {
        id: 'dayOfMonth',
        label: 'Day of Month',
        type: 'number',
        min: 1,
        max: 31,
        required: (config) => config.schedule === 'Monthly',
        showWhen: (config) => config.schedule === 'Monthly'
      },
      {
        id: 'cronExpression',
        label: 'Cron Expression',
        type: 'text',
        placeholder: '0 0 * * *',
        required: (config) => config.schedule === 'Custom Cron',
        showWhen: (config) => config.schedule === 'Custom Cron'
      }
    ]
  },
  dashboardGenerator: {
    title: 'Dashboard Generator Configuration',
    fields: [
      {
        id: 'title',
        label: 'Dashboard Title',
        type: 'text',
        placeholder: 'Sales Performance Dashboard',
        required: true
      },
      {
        id: 'layout',
        label: 'Layout',
        type: 'select',
        options: ['Grid', 'Flexible', 'Tabs', 'Vertical Sections'],
        required: true
      },
      {
        id: 'theme',
        label: 'Theme',
        type: 'select',
        options: ['Light', 'Dark', 'Corporate', 'Colorful'],
        required: false
      },
      {
        id: 'refreshInterval',
        label: 'Refresh Interval (seconds)',
        type: 'number',
        min: 0,
        max: 3600,
        required: false
      },
      {
        id: 'isInteractive',
        label: 'Interactive Dashboard',
        type: 'checkbox',
        required: false
      }
    ]
  },
  chartBuilder: {
    title: 'Chart Builder Configuration',
    fields: [
      {
        id: 'chartType',
        label: 'Chart Type',
        type: 'select',
        options: ['Bar', 'Line', 'Pie', 'Area', 'Scatter', 'Bubble', 'Radar', 'Heatmap', 'Table'],
        required: true
      },
      {
        id: 'title',
        label: 'Chart Title',
        type: 'text',
        placeholder: 'Monthly Sales',
        required: true
      },
      {
        id: 'xAxis',
        label: 'X-Axis Field',
        type: 'text',
        placeholder: 'month',
        required: (config) => ['Bar', 'Line', 'Area', 'Scatter', 'Bubble'].includes(config.chartType),
        showWhen: (config) => ['Bar', 'Line', 'Area', 'Scatter', 'Bubble'].includes(config.chartType)
      },
      {
        id: 'yAxis',
        label: 'Y-Axis Field',
        type: 'text',
        placeholder: 'sales',
        required: (config) => ['Bar', 'Line', 'Area', 'Scatter', 'Bubble'].includes(config.chartType),
        showWhen: (config) => ['Bar', 'Line', 'Area', 'Scatter', 'Bubble'].includes(config.chartType)
      },
      {
        id: 'categoryField',
        label: 'Category Field',
        type: 'text',
        placeholder: 'product',
        required: (config) => ['Pie'].includes(config.chartType),
        showWhen: (config) => ['Pie'].includes(config.chartType)
      },
      {
        id: 'valueField',
        label: 'Value Field',
        type: 'text',
        placeholder: 'sales',
        required: (config) => ['Pie'].includes(config.chartType),
        showWhen: (config) => ['Pie'].includes(config.chartType)
      },
      {
        id: 'colorScheme',
        label: 'Color Scheme',
        type: 'select',
        options: ['Default', 'Categorical', 'Sequential', 'Diverging', 'Custom'],
        required: false
      }
    ]
  },
  reportExporter: {
    title: 'Report Exporter Configuration',
    fields: [
      {
        id: 'format',
        label: 'Export Format',
        type: 'select',
        options: ['PDF', 'Excel', 'CSV', 'HTML', 'JSON'],
        required: true
      },
      {
        id: 'filename',
        label: 'Filename',
        type: 'text',
        placeholder: 'monthly_report_{{date}}',
        required: true
      },
      {
        id: 'includeCharts',
        label: 'Include Charts',
        type: 'checkbox',
        required: false
      },
      {
        id: 'includeRawData',
        label: 'Include Raw Data',
        type: 'checkbox',
        required: false
      },
      {
        id: 'password',
        label: 'Password Protection',
        type: 'password',
        required: false,
        showWhen: (config) => config.format === 'PDF' || config.format === 'Excel'
      }
    ]
  },
  kpiTracker: {
    title: 'KPI Tracker Configuration',
    fields: [
      {
        id: 'kpiName',
        label: 'KPI Name',
        type: 'text',
        placeholder: 'Monthly Revenue',
        required: true
      },
      {
        id: 'metricField',
        label: 'Metric Field',
        type: 'text',
        placeholder: 'revenue',
        required: true
      },
      {
        id: 'targetValue',
        label: 'Target Value',
        type: 'number',
        placeholder: '10000',
        required: true
      },
      {
        id: 'comparisonType',
        label: 'Comparison Type',
        type: 'select',
        options: ['Greater Than', 'Less Than', 'Equal To', 'Between'],
        required: true
      },
      {
        id: 'timeGranularity',
        label: 'Time Granularity',
        type: 'select',
        options: ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly'],
        required: true
      },
      {
        id: 'alertThreshold',
        label: 'Alert Threshold (%)',
        type: 'number',
        min: 1,
        max: 100,
        placeholder: '90',
        required: false
      }
    ]
  }
}; 